import { inject } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { catchError, switchMap, throwError, EMPTY } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { TokenStorageService } from '../services/token-storage.service';

/**
 * Authentication HTTP Interceptor (Functional)
 * Automatically attaches JWT tokens to outgoing requests
 * Handles 401 errors with automatic token refresh
 * Uses modern Angular functional interceptor approach
 */
export const authInterceptorFn: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  const authService = inject(AuthService);
  const tokenStorage = inject(TokenStorageService);
  const router = inject(Router);

  // Skip auth for certain endpoints
  if (shouldSkipAuth(req)) {
    return next(req);
  }

  // Get valid access token
  const accessToken = tokenStorage.getValidAccessToken();
  const activeVenueId = tokenStorage.getActiveVenueId();

  // Clone request with auth header if token exists
  const authReq = accessToken
    ? addAuthHeaders(req, accessToken, activeVenueId)
    : req;

  // Handle the request
  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      // Handle 401 Unauthorized errors
      if (error.status === 401 && accessToken) {
        return handle401Error(req, next, authService, tokenStorage, router);
      }

      // Handle other auth-related errors
      if (error.status === 403 && !isVenueBootstrapRequest(req)) {
        router.navigate(['/unauthorized']);
        return EMPTY;
      }

      return throwError(() => error);
    })
  );
};

/**
 * Add Authorization header to request
 */
function addAuthHeaders(
  req: HttpRequest<unknown>,
  token: string,
  activeVenueId: number | null,
): HttpRequest<unknown> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${token}`,
  };

  if (activeVenueId && !isVenueBootstrapRequest(req)) {
    headers['X-Venue-Id'] = activeVenueId.toString();
  }

  return req.clone({
    setHeaders: headers,
  });
}

/**
 * Handle 401 errors with token refresh
 */
function handle401Error(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  tokenStorage: TokenStorageService,
  router: Router
) {
  // Try to refresh token
  return authService.refreshToken().pipe(
    switchMap(() => {
      // Get new token and retry request
      const newToken = tokenStorage.getValidAccessToken();

      if (newToken) {
        const newAuthReq = addAuthHeaders(
          req,
          newToken,
          tokenStorage.getActiveVenueId() ??
          tokenStorage.getActiveVenueContext()?.businessId ??
          null,
        );
        return next(newAuthReq);
      } else {
        // No valid token after refresh, redirect to login
        router.navigate(['/sign-in']);
        return EMPTY;
      }
    }),
    catchError((refreshError) => {
      // Refresh failed, logout and redirect
      console.error('Token refresh failed:', refreshError);
      authService.logout().subscribe();
      return EMPTY;
    })
  );
}

function isVenueBootstrapRequest(req: HttpRequest<unknown>): boolean {
  return req.url.includes('/venue-context/');
}

/**
 * Determine if request should skip authentication
 */
function shouldSkipAuth(req: HttpRequest<unknown>): boolean {
  const skipAuthUrls = [
    '/auth/login',
    '/auth/register',
    '/auth/refresh',
    '/auth/forgot-password',
    '/auth/reset-password',
    '/public',
    'https://sand-api.monoova.com/'
  ];

  return skipAuthUrls.some(url => req.url.includes(url)) ||
    req.headers.has('Skip-Auth') ||
    req.method === 'OPTIONS';
}

/**
 * Retry interceptor for handling network errors
 * Can be used alongside auth interceptor
 */
export const retryInterceptorFn: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Retry on network errors (but not auth errors)
      if (error.status === 0 && !req.url.includes('/auth/')) {
        // Could implement retry logic here
        console.warn('Network error detected:', error);
      }
      return throwError(() => error);
    })
  );
};

/**
 * Loading interceptor for showing loading states
 * Can be used alongside auth interceptor
 */
export const loadingInterceptorFn: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  // Could inject a loading service here to show/hide loading indicators
  const startTime = Date.now();

  return next(req).pipe(
    catchError((error) => {
      const duration = Date.now() - startTime;
      console.debug(`Request failed after ${duration}ms:`, req.url);
      return throwError(() => error);
    })
  );
};
